import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  TrendingUp, 
  CalendarDays, 
  Truck, 
  MapPin,
  DollarSign,
  Clock,
  Users,
  CheckCircle,
  Percent,
  Inbox,
  Shield,
  Share2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [supportOverview, setSupportOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);

      const supportResponse = await fetch(`${API_URL}/api/admin/support-overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (supportResponse.ok) {
        setSupportOverview(await supportResponse.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', variant: 'secondary', className: 'status-pending' },
      paid: { label: 'Bezahlt', variant: 'default', className: 'status-paid' },
      cancelled: { label: 'Storniert', variant: 'destructive', className: 'status-cancelled' },
      expired: { label: 'Abgelaufen', variant: 'outline', className: 'status-expired' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const statCards = [
    {
      title: t('admin.stats.totalBookings'),
      value: stats?.total_bookings || 0,
      icon: CalendarDays,
      color: 'text-[hsl(var(--primary))]',
      bgColor: 'bg-[hsl(var(--primary)/0.1)]'
    },
    {
      title: 'Bestätigte Buchungen',
      value: stats?.confirmed_bookings || 0,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: t('admin.stats.pendingBookings'),
      value: stats?.pending_bookings || 0,
      icon: Clock,
      color: 'text-[hsl(var(--chart-3))]',
      bgColor: 'bg-[hsl(var(--chart-3)/0.1)]'
    },
    {
      title: t('admin.stats.totalRevenue'),
      value: `CHF ${formatPrice(stats?.total_revenue || 0)}`,
      icon: DollarSign,
      color: 'text-[hsl(var(--chart-2))]',
      bgColor: 'bg-[hsl(var(--chart-2)/0.1)]'
    },
    {
      title: 'Auslastung (30 Tage)',
      value: `${stats?.occupancy_rate || 0}%`,
      icon: Percent,
      color: 'text-[hsl(var(--primary))]',
      bgColor: 'bg-[hsl(var(--primary)/0.1)]'
    },
    {
      title: 'Kunden',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'text-[hsl(var(--chart-5))]',
      bgColor: 'bg-[hsl(var(--chart-5)/0.1)]'
    },
    {
      title: 'Offene Anfragen',
      value: stats?.open_inquiries || 0,
      icon: Inbox,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      title: 'Social Freigaben',
      value: stats?.social_pending || 0,
      icon: Share2,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      title: t('admin.stats.totalTrailers'),
      value: stats?.total_trailers || 0,
      icon: Truck,
      color: 'text-[hsl(var(--chart-4))]',
      bgColor: 'bg-[hsl(var(--chart-4)/0.1)]'
    },
    {
      title: t('admin.stats.totalLocations'),
      value: stats?.total_locations || 0,
      icon: MapPin,
      color: 'text-[hsl(var(--chart-5))]',
      bgColor: 'bg-[hsl(var(--chart-5)/0.1)]'
    },
  ];

  return (
    <div>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="card-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{stat.title}</p>
                      <p className="text-2xl font-semibold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Bookings */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle>{t('admin.recentBookings')}</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_bookings?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Kunde</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Standort</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Zeitraum</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Betrag</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_bookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-[hsl(var(--border))] last:border-0">
                      <td className="py-3 px-4">
                        <p className="font-medium">{booking.customer_name}</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{booking.customer_email}</p>
                      </td>
                      <td className="py-3 px-4">{booking.location?.name || '-'}</td>
                      <td className="py-3 px-4 text-sm">
                        {format(new Date(booking.start_date), 'dd.MM.yy', { locale: de })} - {format(new Date(booking.end_date), 'dd.MM.yy', { locale: de })}
                      </td>
                      <td className="py-3 px-4 font-medium">CHF {formatPrice(booking.total_price)}</td>
                      <td className="py-3 px-4">{getStatusBadge(booking.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
              <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Noch keine Buchungen vorhanden</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-8" data-testid="admin-support-overview">
        <Card className="card-shadow xl:col-span-2">
          <CardHeader>
            <CardTitle>Support & operative Queue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Anfragen offen', value: supportOverview?.counts?.open_inquiries || 0 },
                { label: 'Offerten offen', value: supportOverview?.counts?.waiting_offers || 0 },
                { label: 'Reservierungen offen', value: supportOverview?.counts?.pending_bookings || 0 },
                { label: 'Social Fehler', value: supportOverview?.counts?.social_errors || 0 },
              ].map((item) => (
                <div key={item.label} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-4" data-testid={`support-count-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{item.label}</p>
                  <p className="text-2xl font-semibold mt-1">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {(supportOverview?.pending_bookings || []).slice(0, 4).map((item) => (
                <div key={item.id} className="rounded-lg border border-[hsl(var(--border))] px-4 py-3 flex items-center justify-between gap-3" data-testid={`support-booking-${item.id}`}>
                  <div>
                    <p className="font-medium">{item.customer_name} · {item.location_name}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.status === 'paid' ? 'Bezahlt, wartet auf Bestätigung' : 'Reservierung wartet auf Zahlung'} · CHF {formatPrice(item.total_price || 0)}</p>
                  </div>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">{item.created_at ? format(new Date(item.created_at), 'dd.MM.yyyy', { locale: de }) : '-'}</span>
                </div>
              ))}
              {(!supportOverview?.pending_bookings || supportOverview.pending_bookings.length === 0) && (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Aktuell keine offenen Buchungsaufgaben.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Rollen & Freigaben</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(supportOverview?.role_changes || []).slice(0, 4).map((entry) => (
              <div key={entry.id} className="rounded-lg border border-[hsl(var(--border))] px-3 py-3" data-testid={`dashboard-role-change-${entry.id}`}>
                <p className="text-sm font-medium">{entry.details?.target_user || 'Benutzer'}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">{entry.details?.old_role || '-'} → {entry.details?.new_role || '-'} · {entry.admin_name || 'Admin'}</p>
              </div>
            ))}
            {(!supportOverview?.role_changes || supportOverview.role_changes.length === 0) && (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Noch keine Rollenänderungen.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
